import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Skeleton,
  Stack,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as Yup from "yup";
import SuccessModal from "views/Components/SuccessModal";
import ErrorModal from "views/Components/ErrorModal";
import WarningModal from "views/Components/WarningModal";
import AlertConfirmacion from "views/Components/AlertConfirmacion";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import useTableNames from "../Queries/useTableNamesQuery";
import useTableItems from "../Queries/useTableItemsQuery";
import {
  useAddTableItemMutation,
  useUpdateTableItemMutation,
  useDeleteTableItemMutation,
} from "../Queries/useTableItemMutations";

const ItemSchema = Yup.object().shape({});

function SkeletonsEditConst() {
  return (
    <Stack spacing={3} mt={1}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <Skeleton variant="rounded" width="50%" height={56} />
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Skeleton variant="rounded" width={120} height={40} />
        <Skeleton variant="rounded" width={170} height={40} />
      </Box>
    </Stack>
  );
}

export default function EditConst({ mainCountry }) {
  const [successOpen, setSuccessOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [tablaHasBeenSelected, setTablaHasBeenSelected] = useState(false);
  const [itemHasBeenSelected, setItemHasBeenSelected] = useState(false);
  const [itemHasBeenCreatedOrEdited, setItemHasBeenCreatedOrEdited] =
    useState(false);
  const [displayConfirmModal, setDisplayConfirmModal] = useState({
    open: false,
    message: "",
  });
  const [itemDuplicated, setItemDuplicated] = useState(false);
  const [selectedTableName, setSelectedTableName] = useState(null);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: listaTablas = [], isLoading: tablesLoading } = useTableNames();
  const { data: itemsTabla = [], isLoading: itemsLoading } = useTableItems({
    tableName: selectedTableName,
    countryId: mainCountry,
  });
  const addMutation = useAddTableItemMutation();
  const updateMutation = useUpdateTableItemMutation();
  const deleteMutation = useDeleteTableItemMutation();
  const isSubmitting =
    addMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  const listaAcciones = [
    { label: "Agregar", id: 0 },
    { label: "Habilitar / Deshabilitar", id: 1 },
    { label: "Editar", id: 2 },
  ];

  const formik = useFormik({
    initialValues: {
      tablaElegida: null,
      itemElegido: null,
      itemEditado: null,
      editMode: -1,
      esTaller: false,
    },
    validationSchema: ItemSchema,
    onSubmit: async (values) => {
      try {
        const tableName = values.tablaElegida?.name;
        const mutationVars = { tableName, countryId: mainCountry };
        let statusCode;

        if (
          values.editMode === 2 ||
          (values.editMode === 1 && !values?.itemEditado?.active)
        ) {
          statusCode = await updateMutation.mutateAsync({
            ...mutationVars,
            item: { ...values.itemEditado, active: true },
          });
        } else if (values.editMode === 0) {
          statusCode = await addMutation.mutateAsync({
            ...mutationVars,
            item: values.itemEditado,
          });
        } else {
          statusCode = await deleteMutation.mutateAsync({
            ...mutationVars,
            item: values.itemEditado,
          });
        }

        setDisplayConfirmModal({ open: false, message: "" });

        if (statusCode === 200) {
          setSuccessOpen(true);
          formik.resetForm();
          setTablaHasBeenSelected(false);
          setItemHasBeenSelected(false);
          setItemHasBeenCreatedOrEdited(false);
          setSelectedTableName(null);
        } else if (statusCode === 403) {
          setWarningOpen(true);
          setWarningMessage("No autorizado");
        } else {
          setErrorOpen(true);
          setErrorMessage("Error desde el servidor — Comunicarse con soporte");
        }
      } catch (error) {
        console.error("Error", error);
        setErrorOpen(true);
        setErrorMessage("Error al Editar/Agregar/Borrar el item");
      }
    },
  });

  const handleTablaChange = (event, newValue, reason) => {
    if (reason === "clear") {
      formik.setFieldValue("tablaElegida", null);
      formik.setFieldValue("itemElegido", null);
      formik.setFieldValue("itemEditado", null);
      formik.setFieldValue("editMode", -1);
      setTablaHasBeenSelected(false);
      setSelectedTableName(null);
    } else {
      const tablaElegida =
        newValue ?? listaTablas.find((s) => s.id === 1) ?? null;
      formik.setFieldValue("tablaElegida", tablaElegida);
      formik.setFieldValue("itemEditado", null);
      formik.setFieldValue("itemElegido", null);
      setTablaHasBeenSelected(true);
      setSelectedTableName(tablaElegida?.name ?? null);
    }
  };

  const handleItemChange = (event, newValue, reason) => {
    if (reason === "clear") {
      formik.setFieldValue("itemEditado", null);
      formik.setFieldValue("itemElegido", null);
      setItemHasBeenCreatedOrEdited(false);
      setItemHasBeenSelected(false);
    } else {
      const itemElegido = newValue ?? itemsTabla.find((s) => s.id === 1);
      formik.setFieldValue("itemEditado", itemElegido);
      formik.setFieldValue("itemElegido", itemElegido);
      setItemHasBeenSelected(true);
    }
  };

  const getFecha = () => new Date().toISOString();

  const handleItemAdd = (event) => {
    const itemCreado = {
      id: 0,
      name: event.target.value,
      active: true,
      date_created: getFecha(),
      update_date: getFecha(),
      isworkshop: formik?.values?.esTaller,
      country_id: mainCountry,
    };
    formik.setFieldValue("itemEditado", itemCreado);
    setItemDuplicated(!!itemsTabla.find((x) => x.name === itemCreado.name));
    setItemHasBeenCreatedOrEdited(
      event?.target?.value !== "" && event?.target?.value != null,
    );
  };

  const handleChangeAccion = (event, newValue, reason) => {
    formik.setFieldValue("editMode", reason === "clear" ? -1 : newValue?.id);
    formik.setFieldValue("itemElegido", null);
    formik.setFieldValue("itemEditado", null);
    setItemHasBeenSelected(false);
    setItemHasBeenCreatedOrEdited(false);
  };

  const handleItemEdit = (event) => {
    formik.setFieldValue("itemEditado", {
      ...formik.values.itemEditado,
      name: event.target.value,
    });
    const isEmpty = event?.target?.value === "" || event?.target?.value == null;
    setItemHasBeenCreatedOrEdited(!isEmpty);
    setItemDuplicated(
      !!itemsTabla.find((x) => x.name === event?.target?.value),
    );
  };

  const handleChangeCheckBox = (event) => {
    formik.setFieldValue("esTaller", event.target.checked);
    formik.setFieldValue("itemEditado", {
      ...formik?.values?.itemEditado,
      isworkshop: event.target.checked,
    });
    setItemHasBeenCreatedOrEdited(true);
    setItemDuplicated(
      event?.target?.checked === formik?.values?.itemElegido?.isworkshop,
    );
  };

  const getTextoBoton = (values) => {
    if (values?.editMode === 0 || values?.editMode === 2)
      return "GUARDAR CAMBIOS";
    return values?.itemElegido?.active ? "DesHabilitar" : "Habilitar";
  };

  const cleanConstants = () => {
    formik.resetForm();
    setTablaHasBeenSelected(false);
    setItemHasBeenSelected(false);
    setItemHasBeenCreatedOrEdited(false);
    setItemDuplicated(false);
    setDisplayConfirmModal({ open: false, message: "" });
  };

  return (
    <Box sx={{ height: "60vh" }}>
      <AlertConfirmacion
        handleClickOpen={displayConfirmModal.open}
        handleClose={() => setDisplayConfirmModal({ open: false, message: "" })}
        handleConfirmacion={formik.handleSubmit}
        message={displayConfirmModal.message}
      />
      <SuccessModal
        handleClose={() => setSuccessOpen(false)}
        open={successOpen}
        message="La operación fue un éxito!"
      />
      <WarningModal
        handleClose={() => setWarningOpen(false)}
        open={warningOpen}
        message="Ups, algo ocurrió"
        message2={warningMessage}
      />
      <ErrorModal
        handleClose={() => setErrorOpen(false)}
        open={errorOpen}
        message="Error"
        message2={errorMessage}
      />

      {tablesLoading && <SkeletonsEditConst />}

      {/* Overlay solo durante submit */}
      <div
        className="form_overlay"
        style={{ display: isSubmitting ? "flex" : "none" }}
      >
        <CircularProgress />
      </div>

      {!tablesLoading && (
        <form
          onSubmit={formik.handleSubmit}
          noValidate
          style={{
            height: "50%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disablePortal
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    backgroundColor: "#fff !important",
                    borderRadius: "8px",
                    WebkitBoxShadow: "0 0 0 1000px white inset",
                  },
                }}
                fullWidth
                id="combo-box-tablas"
                options={listaTablas}
                getOptionLabel={(option) => option.name}
                onChange={handleTablaChange}
                renderInput={(params) => (
                  <TextField {...params} label="Elija Tabla:" />
                )}
                value={formik?.values?.tablaElegida}
              />
            </Grid>

            <Grid item xs={12} md={6} hidden={!tablaHasBeenSelected}>
              <Autocomplete
                disablePortal
                fullWidth
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    backgroundColor: "#fff !important",
                    borderRadius: "8px",
                    WebkitBoxShadow: "0 0 0 1000px white inset",
                  },
                }}
                id="combo-box-acciones"
                options={listaAcciones}
                onChange={handleChangeAccion}
                renderInput={(params) => (
                  <TextField {...params} label="Elija Accion" />
                )}
                value={
                  formik?.values?.editMode >= 0
                    ? listaAcciones[formik?.values?.editMode]
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12} md={12} hidden={formik?.values?.editMode <= 0}>
              {itemsLoading ? (
                <Skeleton variant="rounded" width="100%" height={56} />
              ) : (
                <Autocomplete
                  disablePortal
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    },
                    "& .MuiInputBase-input": {
                      backgroundColor: "#fff !important",
                      borderRadius: "8px",
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                  fullWidth
                  id="combo-box-items"
                  options={itemsTabla}
                  getOptionLabel={(option) => option.name}
                  onChange={handleItemChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Items Disponibles:" />
                  )}
                  renderOption={(props, option) => (
                    <span
                      {...props}
                      style={{
                        backgroundColor: option.active ? "white" : "lightgrey",
                      }}
                    >
                      {option.name}
                    </span>
                  )}
                  value={formik?.values?.itemElegido}
                />
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={12}
              hidden={!(tablaHasBeenSelected && formik?.values?.editMode === 0)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "70%",
                  columnGap: "10px",
                }}
              >
                <TextField
                  fullWidth
                  sx={{
                    display:
                      tablaHasBeenSelected && formik?.values?.editMode === 0
                        ? ""
                        : "none",

                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-input": {
                      backgroundColor: "#fff !important",
                      borderRadius: "8px",
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                  id="itemIngresado"
                  label="Ingrese nuevo item:"
                  value={
                    formik?.values?.itemEditado?.name != null
                      ? formik.values.itemEditado.name
                      : ""
                  }
                  onChange={handleItemAdd}
                  helperText={
                    itemDuplicated
                      ? "YA EXISTE ... Ayuda: Si no se muestra en la app, prueba la opcion hab. item"
                      : ""
                  }
                  FormHelperTextProps={{
                    style: { color: "red", textAlign: "center" },
                  }}
                />
              </Box>
              <Box
                hidden={
                  formik?.values?.tablaElegida?.name !== "deposito_destino"
                }
              >
                <FormControlLabel
                  label="Es Taller ?"
                  control={
                    <Checkbox
                      checked={formik?.values?.esTaller}
                      onChange={handleChangeCheckBox}
                    />
                  }
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={12} hidden={formik?.values?.editMode < 2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "70%",
                  columnGap: "10px",
                }}
              >
                <TextField
                  fullWidth
                  id="itemEditado"
                  label="Editando item:"
                  value={
                    formik?.values?.itemEditado?.name != null
                      ? formik.values.itemEditado.name
                      : ""
                  }
                  onChange={handleItemEdit}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    },
                    "& .MuiOutlinedInput-input": {
                      backgroundColor: "#fff !important",
                      borderRadius: "8px",
                      WebkitBoxShadow: "0 0 0 1000px white inset",
                    },
                  }}
                />
              </Box>
              <Box
                hidden={
                  formik?.values?.tablaElegida?.name !== "deposito_destino"
                }
              >
                <FormControlLabel
                  label="Es Taller ?"
                  control={
                    <Checkbox
                      checked={formik?.values?.itemEditado?.isworkshop ?? false}
                      onChange={handleChangeCheckBox}
                    />
                  }
                />
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              columnGap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={cleanConstants}
              style={{
                color: "#002878",
                fontWeight: 500,
                borderColor: "#002878",
                "&:hover": { borderColor: "#002878" },
                width: "120px",
                textTransform: "uppercase",
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={
                itemDuplicated ||
                (formik?.values?.editMode !== 1 &&
                  !itemHasBeenCreatedOrEdited) ||
                (formik?.values?.editMode === 1 && !itemHasBeenSelected)
              }
              style={{
                color:
                  itemDuplicated ||
                  (formik?.values?.editMode !== 1 &&
                    !itemHasBeenCreatedOrEdited) ||
                  (formik?.values?.editMode === 1 && !itemHasBeenSelected)
                    ? "rgba(0, 0, 0, 0.26)"
                    : "white",
                backgroundColor:
                  itemDuplicated ||
                  (formik?.values?.editMode !== 1 &&
                    !itemHasBeenCreatedOrEdited) ||
                  (formik?.values?.editMode === 1 && !itemHasBeenSelected)
                    ? "#0000001F"
                    : "#002878",
                width: "170px",
                textTransform: "uppercase",
              }}
              onClick={() =>
                setDisplayConfirmModal({
                  open: true,
                  message: "enviar cambios",
                })
              }
            >
              {getTextoBoton(formik.values)}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
}
